import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Container,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import gd from 'date-fns/locale/gd/index.js';
import { m } from 'framer-motion';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { varFade } from 'src/components/animate';
import Carousel, { CarouselArrows, useCarousel } from 'src/components/carousel';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { LoadingScreen } from 'src/components/loading-screen';
import { useSettingsContext } from 'src/components/settings';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { useLocales } from 'src/locales';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import {
  useGetAvailableGiveawayForParticipationQuery,
  useLazyGetGiveawayAdsByIdQuery,
} from 'src/store/Reducer/giveaway';
import { fDaysLeft } from 'src/utils/format-time';

const Giveaway = () => {
  const { t } = useLocales();
  const Theme = useTheme();
  const settings = useSettingsContext();
  const is500px = useMediaQuery('(min-width:600px)');
  const router = useRouter();
  const { user } = useMockedUser();
  const [clickId, setClickId] = useState(null);
  const skipApi = user?.userType !== 'user';
  const [triggerGetGiveawayAdsById, { isLoading: adLoading }] = useLazyGetGiveawayAdsByIdQuery();
  const [category, setCategory] = useState({
    id: '',
    name: '',
    index: 0,
    limit: 10,
    page: 1,
  });
  let {
    data: availableGiveaway,
    isLoading,
    refetch,
  } = useGetAvailableGiveawayForParticipationQuery({
    page: category.page,
    limit: category.limit,
  });
  const [aGiveaways, setAGiveaways] = useState([]);
  const setInitialData = () => {
    setAGiveaways(availableGiveaway?.data?.filter((item) => item?.giveaways?.length > 0) || []);
  };

  const setNewData = () => {
    const updatedGiveaways = [...aGiveaways];
    const idx = category?.index;
    if (updatedGiveaways[idx]) {
      updatedGiveaways[idx] = {
        ...updatedGiveaways[idx],
        giveaways: [
          ...(updatedGiveaways[idx]?.giveaways || []),
          ...(availableGiveaway?.data || []),
        ],
        meta: availableGiveaway?.meta || updatedGiveaways[idx]?.meta,
      };
      setAGiveaways(updatedGiveaways);
    }
  };
  const handleNext = (giveawayState, carousel, index) => {
    const { giveaways = [], meta = {}, category = {} } = giveawayState || {};
    const { currentIndex, carouselSettings } = carousel;
    const slidesToShow = carouselSettings?.slidesToShow ?? 1;

    // When currentIndex + slidesToShow >= total slides, you're at the end
    const isLastSlide = currentIndex + slidesToShow >= giveaways.length;
    const hasMorePages = meta.currentPage < meta.totalPages;

    if (isLastSlide && hasMorePages) {
      setCategory({
        id: category._id,
        name: category.name,
        index: index,
        limit: category.limit ?? carouselSettings.slidesToScroll ?? 10,
        page: (meta.currentPage || 0) + 1,
      });

      // reset carousel to the start of the new data
      carousel.goTo(0);
      return; // stop here so it doesn’t call `onNext()` unnecessarily
    }

    carousel.onNext();
  };

  useEffect(() => {
    if (!category.id) {
      setInitialData();
    } else {
      setNewData();
    }
  }, [availableGiveaway]);

  const carousel = useCarousel({
    slidesToShow: 4,
    slidesToScroll: 1,
    cssEase: 'linear',
    centerPadding: '60px',
    infinite: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 600,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1, centerPadding: '0' },
      },
    ],
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  let render404 = (
    <Grid
      item
      xs={12}
      md={12}
      sx={{
        textAlign: 'center',
        mt: 5,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center !important',
        alignItems: 'center !important',
      }}
    >
      <Typography variant="h5" color="text.secondary">
        {/* No Giveaways Available at the Moment */}
        {t('home.noGiveaway')}
      </Typography>
      <Typography variant="body1" paragraph color="text.secondary">
        {/* Please check back later for new giveaways. */}
        {t('home.noGiveawayDesc')}
      </Typography>
      <Avatar
        src="/assets/images/landing-page/no-giveaways.png"
        alt=""
        sx={{
          height: { xs: '100px', md: '200px' },
          objectFit: 'cover',
          width: { xs: '100px', md: '200px' },
          margin: '0 auto',
        }}
      />
    </Grid>
  );

  const handleParticipate = async (id, index) => {
    setClickId(index);
    if (user.userType === 'user') {
      const response = await triggerGetGiveawayAdsById({ id, skipApi: skipApi });
      if (!response?.error) {
        if (response?.data?.data?.length > 0) {
          router.push(`${paths.dashboard.giveawayDetail}/${id}`, { state: { user: true } });
        } else {
          enqueueSnackbar(
            "You can't participate in this giveaway because it has expired or no ad is available.",
            { variant: 'error' }
          );
        }
      }
    } else {
      router.push(`${paths.dashboard.giveawayDetail}/${id}`);
    }
  };

  return (
    <>
      {isLoading ? (
        <>
          <LoadingScreen />
        </>
      ) : (
        <Container maxWidth={settings.themeStretch ? false : 'xl'}>
          <Box sx={{ position: 'relative', overflow: 'hidden', py: 5 }}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: {
                  xs: '90%', // small screens
                  sm: 300,
                  md: 400, // medium screens
                  lg: 800, // large screens
                },
                height: {
                  xs: '90%',
                  sm: 300,
                  md: 400,
                  lg: 800,
                },
                backgroundImage: `url(/assets/images/landing-page/${
                  Theme.palette.mode === 'dark' ? 'logoImage.png' : 'logoLightMode.png'
                })`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                zIndex: 100,
                opacity: 0.1,
                pointerEvents: 'none',
              }}
            />

            <CustomBreadcrumbs
              heading={t('home.participate_in_giveaway')}
              links={[
                { href: '/dashboard', name: t('home.dashboard') },
                { name: t('home.participate_in_giveaway') },
              ]}
              sx={{ mb: 3 }}
            />
            {aGiveaways?.map((g, index) => (
              <Grid container>
                <Box sx={{ width: { xs: '100%', md: '95%', lg: '95%' }, marginX: 'auto' }}>
                  <Typography
                    variant="h4"
                    textAlign={{ xs: 'center', md: 'left' }}
                    fontWeight={700}
                    my={2}
                  >
                    {/* {t('home.experiences')} */}
                    {g?.category?.title || 'No Category'}
                  </Typography>
                </Box>
                {Array.isArray(g?.giveaways) &&
                g?.giveaways.length > 3 ? (
                  <Grid item xs={12} md={12} component={m.div} variants={varFade().inUp}>
                    <Box sx={{ textAlign: 'end', mb: 1, mr: 2 }}>
                      <CarouselArrows
                        onNext={() => handleNext(g, carousel,index)}
                        onPrev={carousel.onPrev}
                        leftButtonProps={{
                          sx: {
                            left: 0,
                            zIndex: 1,
                            bgcolor: 'grey.100',
                            transform: 'translateX(-20%)',
                            color: 'grey.900',
                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                            '&:hover': { bgcolor: 'grey.300' },
                            ...(gd?.giveaways?.length < 5 && { display: 'none' }),
                          },
                        }}
                        rightButtonProps={{
                          sx: {
                            right: 0,
                            zIndex: 1,
                            bgcolor: 'grey.100',
                            transform: 'translateX(20%)',
                            color: 'grey.900',
                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                            '&:hover': { bgcolor: 'grey.300' },
                            ...(g?.giveaways?.length < 5 && { display: 'none' }),
                          },
                        }}
                      ></CarouselArrows>
                    </Box>

                    <Carousel ref={carousel.carouselRef} {...carousel.carouselSettings}>
                      {g?.giveaways?.map((item, index) => (
                        <GiveawayCard
                          key={item._id || index}
                          item={item}
                          is500px={is500px}
                          handleParticipate={handleParticipate}
                          index={index}
                          clickId={clickId}
                          loading={adLoading}
                        />
                      ))}
                    </Carousel>
                  </Grid>
                ) : g?.giveaways.length !== 0 ? (
                  g?.giveaways?.map((item, index) => (
                    <Grid
                      key={item._id}
                      item
                      xs={12}
                      md={3}
                      sx={{ mb: 2 }}
                      component={m.div}
                      variants={varFade().inUp}
                    >
                      <GiveawayCard
                        item={item}
                        is500px={is500px}
                        handleParticipate={handleParticipate}
                        loading={adLoading}
                        index={index}
                        clickId={clickId}
                      />
                    </Grid>
                  ))
                ) : (
                  render404
                )}
              </Grid>
            ))}
          </Box>
        </Container>
      )}
    </>
  );
};

export default Giveaway;

const GiveawayCard = ({ item, is500px, handleParticipate, loading, clickId, index }) => {
  const { user } = useMockedUser();

  const notLoggedIn = !user;
  let buttonLabel = 'Participate Now';
  let disabled = false;
  let tooltipMessage = '';

  if (notLoggedIn) {
    buttonLabel = 'Participate Now';
    disabled = true;
    tooltipMessage = 'Please login to join this giveaway';
  } else if (!item?.hasAd) {
    buttonLabel = 'Participate Now';
    disabled = true;
    tooltipMessage = 'This giveaway has no ad associated with it';
  }
  return (
    <Box
      sx={{
        bgcolor: 'black',
        zIndex: 200,
        p: 2,
        mx: { md: 2, xs: 1 },
        borderRadius: 2,
        boxSizing: 'border-box',
        marginBottom: { md: 0, xs: 2 },
      }}
    >
      <Box
        sx={{
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
          overflow: 'hidden',
          maxWidth: 300,
          width: '100%',
          margin: '0 auto',
          height: { xs: 200, sm: 200, md: 200 },
          backgroundImage: `url(${
            item?.basicInfo?.image ===
            'https://LuckyAdsappbucket.s3.us-east-1.amazonaws.com/summer-giveaway.png'
              ? '/assets/images/landing-page/giveaway1.png'
              : `${item?.basicInfo?.image || item?.Image}`
          })`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(1.05)',
        }}
      />
      <Box
        sx={{
          width: '100%',
          color: 'white',
          mt: 2,
        }}
      >
        <Typography variant="h6" noWrap>
          {item?.basicInfo?.title?.length > 20
            ? item?.basicInfo?.title.slice(0, 20) + '...'
            : item?.basicInfo?.title}
        </Typography>
        <Typography variant="body2" fontWeight={300}>
          <div
            style={{ direction: 'ltr', color: 'white' }}
            dangerouslySetInnerHTML={{
              __html:
                item?.basicInfo?.description.length > 30
                  ? item?.basicInfo?.description.slice(0, 30) + '...'
                  : item?.basicInfo?.description || 'No description added',
            }}
          />
        </Typography>
        <Typography fontSize={is500px ? '1.2rem' : '0.8rem'} fontWeight={400} noWrap>
          <Iconify icon="lucide:users-round" color="primary.main" /> {item.participants?.length}{' '}
          participants
        </Typography>
        <Typography fontSize={is500px ? '1.2rem' : '0.8rem'} color="primary" fontWeight={700}>
          {fDaysLeft(item?.dateSettings?.endDate || item?.dayleft)}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          Price: {item?.basicInfo?.price || 'Not specified'}
        </Typography>

        <Tooltip title={tooltipMessage}>
          <LoadingButton
            fullWidth
            variant="contained"
            loading={loading && clickId === index}
            sx={{
              py: 1,
              mt: 2,
              fontWeight: 500,
              bgcolor: 'primary.dark',
              color: 'black',
              '&:hover': {
                bgcolor: disabled ? 'grey.400' : 'primary.main',
                color: 'white',
                cursor: disabled ? 'not-allowed' : 'pointer',
              },
            }}
            onClick={() => !disabled && handleParticipate(item._id, index)}
          >
            {buttonLabel}
          </LoadingButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
